import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Mail, 
  Phone, 
  Clock, 
  Send, 
  Loader2, 
  CheckCircle,
  ArrowLeft,
  Building2,
  User,
  MessageSquare
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    if (!formData.message.trim()) {
      toast.error("Please enter your message");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/contact/submit`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error(error.response?.data?.detail || "Failed to send message. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      company_name: "",
      message: ""
    });
    setSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
      {/* Header */}
      <div className="border-b border-stone-800 bg-stone-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/images/myhairchair-logo.png" 
              alt="MyHairChair" 
              className="h-10 w-auto"
            />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-stone-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Page Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Contact Us
          </h1>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto">
            Have questions about MyHairChair? We'd love to hear from you. 
            Send us a message and we'll respond within 24-48 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="space-y-6">
            {/* Email Card */}
            <Card className="bg-stone-900/50 border-stone-800 hover:border-amber-500/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email Us</h3>
                    <a 
                      href="mailto:info@myhairchair.com" 
                      className="text-amber-400 hover:text-amber-300 font-medium"
                    >
                      info@myhairchair.com
                    </a>
                    <p className="text-stone-500 text-sm mt-1">
                      For general inquiries
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Phone Card */}
            <Card className="bg-stone-900/50 border-stone-800 hover:border-amber-500/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Call Us</h3>
                    <a 
                      href="tel:+18327261911" 
                      className="text-green-400 hover:text-green-300 font-medium text-lg"
                    >
                      (832) 726-1911
                    </a>
                    <p className="text-stone-500 text-sm mt-1">
                      Business inquiries only
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Business Hours Card */}
            <Card className="bg-stone-900/50 border-stone-800 hover:border-amber-500/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Business Hours</h3>
                    <p className="text-stone-300">Monday - Friday</p>
                    <p className="text-blue-400 font-medium">9:00 AM - 6:00 PM CST</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-3">Quick Links</h3>
                <div className="space-y-2">
                  <Link 
                    to="/faq" 
                    className="block text-stone-300 hover:text-amber-400 text-sm transition-colors"
                  >
                    → Frequently Asked Questions
                  </Link>
                  <Link 
                    to="/membership" 
                    className="block text-stone-300 hover:text-amber-400 text-sm transition-colors"
                  >
                    → Pricing & Plans
                  </Link>
                  <Link 
                    to="/soldiers" 
                    className="block text-stone-300 hover:text-amber-400 text-sm transition-colors"
                  >
                    → Soldier Program
                  </Link>
                  <Link 
                    to="/investors" 
                    className="block text-stone-300 hover:text-amber-400 text-sm transition-colors"
                  >
                    → Investor Relations
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-stone-900/80 border-stone-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-amber-400" />
                  Send Us a Message
                </CardTitle>
                <CardDescription className="text-stone-400">
                  Fill out the form below and we'll get back to you as soon as possible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                    <p className="text-stone-400 mb-6 max-w-md mx-auto">
                      Thank you for reaching out. We've received your message and will respond within 24-48 hours.
                    </p>
                    <div className="flex items-center justify-center gap-4">
                      <Button onClick={resetForm} variant="outline" className="border-stone-600 text-white">
                        Send Another Message
                      </Button>
                      <Link to="/">
                        <Button className="bg-amber-500 hover:bg-amber-600 text-black">
                          Return Home
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-stone-300 flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Full Name <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          required
                          className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 focus:border-amber-500"
                          data-testid="contact-name-input"
                        />
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-stone-300 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email Address <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 focus:border-amber-500"
                          data-testid="contact-email-input"
                        />
                      </div>

                      {/* Phone */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-stone-300 flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Phone Number <span className="text-stone-500 text-xs">(Optional)</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                          className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 focus:border-amber-500"
                          data-testid="contact-phone-input"
                        />
                      </div>

                      {/* Company Name */}
                      <div className="space-y-2">
                        <Label htmlFor="company_name" className="text-stone-300 flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Company Name <span className="text-stone-500 text-xs">(If applicable)</span>
                        </Label>
                        <Input
                          id="company_name"
                          name="company_name"
                          type="text"
                          value={formData.company_name}
                          onChange={handleChange}
                          placeholder="Your Salon or Business Name"
                          className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 focus:border-amber-500"
                          data-testid="contact-company-input"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-stone-300 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Your Message <span className="text-red-400">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="How can we help you? Tell us about your inquiry..."
                        required
                        rows={6}
                        className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 focus:border-amber-500 resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="flex items-center justify-between pt-4">
                      <p className="text-stone-500 text-sm">
                        <span className="text-red-400">*</span> Required fields
                      </p>
                      <Button 
                        type="submit" 
                        disabled={submitting}
                        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold px-8"
                        data-testid="contact-submit-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Additional Info */}
            <div className="mt-6 p-4 bg-stone-900/50 border border-stone-800 rounded-lg">
              <p className="text-stone-400 text-sm text-center">
                <strong className="text-white">For urgent business matters:</strong> Call us at{" "}
                <a href="tel:+18327261911" className="text-amber-400 hover:text-amber-300 font-medium">
                  (832) 726-1911
                </a>{" "}
                during normal business hours (Mon-Fri, 9 AM - 6 PM CST)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
